import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { GridStateChangeV2, GridStateV2, GridUtilsV2, StoreV2 } from '@schenck/rotec-components';
import { saveAs } from 'file-saver';
import { forkJoin, of } from 'rxjs';
import { catchError, filter } from 'rxjs/operators';
import { FileApi, FileInfo } from 'src/api/file.api';

type FileServiceKey = 'BugReport' | 'SupportBundle';

@Injectable()
export class FileBaseService extends StoreV2<GridStateV2<FileInfo>> {
  public selectedIds: string[] = [];

  private results: FileInfo[];
  private readonly serviceName: string;

  constructor(
    protected serviceKey: FileServiceKey,
    protected fileApi: FileApi
  ) {
    super(
      {
        page: 0,
        type: '',
        fulltext: '',
        sort: [],
        filter: [],
        total: 0,
        results: [],
        loading: true,
      },
      `${serviceKey}FileService`
    );
    this.serviceName = `${serviceKey}FileService`;
  }

  public loadMachineFiles(files: FileInfo[]): void {
    this.selectedIds = [];
    this.patchState(`${this.serviceName} got results`, {
      results: files,
      total: files.length,
      loading: false,
    });
    this.results = files;
  }

  public reactToStateChange(changedState: GridStateChangeV2): void {
    const updatedState = {
      ...this.state,
      ...changedState,
    };

    this.patchState('update results', {
      ...updatedState,
      ...GridUtilsV2.getResults(this.results, {
        ...updatedState,
        filterFnMap: { name: GridUtilsV2.multiselectFilter, size: GridUtilsV2.multiselectFilter },
        resultComparator: GridUtilsV2.defaultResultComparator,
      }),
    });
  }

  public downloadMachineFiles(machineId: string): void {
    this.patchState('downloading files...', { loading: true });

    const files$ = this.state.results
      .filter(({ id }) => this.selectedIds.includes(id))
      .map((file) => this.fileApi.getMachineFile$({ id: file.id, name: file.name }, machineId));

    forkJoin(files$)
      .pipe(
        catchError((err: HttpErrorResponse) => {
          this.patchState('files were not downloaded', { loading: false });

          throw Error(err.message);
        })
      )
      .subscribe((files: File[]) => {
        files?.forEach((file) => saveAs(file));
        this.patchState('files were downloaded', { loading: false });
      });
  }

  public get selectedFileNames() {
    return this.state.results
      .filter(({ id }) => this.selectedIds.includes(id))
      .map((file) => file.name);
  }

  public deleteMachineFiles(machineId: string): void {
    this.patchState('deleting files...', { loading: true });

    forkJoin(this.selectedIds.map((id) => this.fileApi.deleteMachineFile$(id, machineId)))
      .pipe(
        catchError(() => {
          this.patchState('got updated results without deleting files', { loading: false });
          return of([]);
        }),
        filter((response: boolean[]) => response?.length && response.every((item) => item))
      )
      .subscribe(() => {
        const filteredFiles = this.state.results?.filter(
          ({ id }) => !this.selectedIds.includes(id)
        );

        this.selectedIds = [];
        this.patchState('got updated results after deleting files', {
          results: filteredFiles,
          total: filteredFiles.length,
          loading: false,
        });
      });
  }
}
